import React, { useMemo, useEffect, useState, useCallback } from "react";
import ProfileModal from "../ProfileModal";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { AUTH_STORAGE_KEY } from "../../socket";
import { loadLabels, createLabel as apiCreateLabel, updateLabel as apiUpdateLabel, deleteLabel as apiDeleteLabel } from "../../api/labelsApi";
import { loadBookmarks as apiLoadBookmarks, createBookmark as apiCreateBookmark, updateBookmark as apiUpdateBookmark, deleteBookmark as apiDeleteBookmark } from "../../api/bookmarksApi";
import StorageProgressBar from "./StorageProgressBar";

const NAV_ITEMS = [
  { key: "inbox", label: "Mail", icon: "fa-envelope", to: "/Kmail" },
  { key: "chat", label: "Chat", icon: "fa-solid fa-comments", to: "/kmail/Krivachat" },
  { key: "call", label: "Call", icon: "fa-solid fa-phone", to: "/kmail/calls" },
  { key: "meeting", label: "Meeting", icon: "fa-solid fa-video", to: "/kmail/meeting" },

  // Divider 1

  { key: "labels", label: "Labels", icon: "fa-tag" },
  { key: "bookmarks", label: "Books", icon: "fa-bookmark" },
  { key: "note", label: "Notes", icon: "fa-solid fa-clipboard", to: "/kmail/notes" },

  // Divider 2

  { key: "nearby", label: "Nearby", icon: "fa-location-dot", to: "/" },
  { key: "addservice", label: "Services", icon: "fa-plus-square", to: "/add-service" },
];

function Sidebar({
  collapsed,
  onToggleCollapse,
  unreadCount,
  inboxCount,
  sentCount,
  noteCount = 0,
  contactsCount = 0,
  starredCount,
  isMobile,
  userFullName,
  role = 3,
  draftsCount = 0,
  trashCount = 0,
  recentEmails = [],
  archiveCount = 0,
  onNavigate,
}) {
  const navigate = useNavigate();
  const [labelsModalOpen, setLabelsModalOpen] = useState(false);
  const [bookmarksModalOpen, setBookmarksModalOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navItems = useMemo(() => {
    const base = [...NAV_ITEMS];
    // role: 1 = Superadmin, 2 = Admin, 3 = User
    // Both Superadmin (1) and Admin (2) should see base menu + admin items
    if (Number(role) === 1 || Number(role) === 2) {
      base.push({ key: "categories", label: "Cats", icon: "fa-tags", to: "/admin/service-categories" });
      base.push({ key: "seocategories", label: "SEO Cats", icon: "fa-tags", to: "/admin/seo-categories" });
      base.push({ key: "fields", label: "Fields", icon: "fa-list-check", to: "/admin/fields" });
      base.push({ key: "users", label: "User", icon: "fa-users", to: "/Kmail/users" });
      base.push({ key: "clients", label: "Client", icon: "fa-solid fa-handshake", to: "/Kmail/clients" });
      base.push({ key: "slider", label: "Slider", icon: "fa-images", to: "/Kmail/slider" });
      base.push({ key: "adminservice", label: "Admin Services", icon: "fa-plus-square", to: "/admin/service" });
      base.push({ key: "Ads", label: "Ads", icon: "fa-plus-square", to: "/admin/advertisements" });

    }
      base.push({ key: "settings", label: "Settings", icon: "fa-cogs", to: "/Kmail/settings" });
      base.push({ key: "profile", label: "Profile", icon: "fa-user", to: "/Kmail/profile" });
      base.push({ key: "lock", label: "Lock", icon: "fa-lock" });
    return base;
  }, [role]);

  const [labels, setLabels] = useState([]);
  const [labelsCollapsed, setLabelsCollapsed] = useState(true);
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksCollapsed, setBookmarksCollapsed] = useState(true);
  const [bookmarkModalOpen, setBookmarkModalOpen] = useState(false);
  const [bookmarkModalName, setBookmarkModalName] = useState("");
  const [bookmarkModalLink, setBookmarkModalLink] = useState("");
  const [editingBookmarkId, setEditingBookmarkId] = useState(null);
  const location = useLocation();
  const [assignments, setAssignments] = useState({});
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelModalName, setLabelModalName] = useState("");
  const [labelModalColor, setLabelModalColor] = useState("#536fb6");
  const [labelModalEmail, setLabelModalEmail] = useState("");
  const [editingLabelId, setEditingLabelId] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetch = async () => {
      try {
        // try to read auth headers from local storage (login stores token)
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const authHeaders = parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;

        if (!authHeaders) {
          setLabels([]);
          return;
        }

        const res = await loadLabels(authHeaders);
        const list = Array.isArray(res?.labels) ? res.labels : [];
        if (!mounted) return;
        setLabels(list.map((l) => ({ ...(l || {}), color: l.color || "#536fb6" })));
        try {
          const bmRes = await apiLoadBookmarks(authHeaders);
          const list = Array.isArray(bmRes?.bookmarks) ? bmRes.bookmarks : [];
          if (!mounted) return;
          setBookmarks(list);
        } catch (e) {
          try {
            const raw = localStorage.getItem("MAIL_BOOKMARKS");
            const parsed = raw ? JSON.parse(raw) : [];
            if (Array.isArray(parsed)) setBookmarks(parsed);
          } catch {
            setBookmarks([]);
          }
        }
      } catch (err) {
        console.error("Failed to load labels", err);
        if (mounted) setLabels([]);
      }
    };

    fetch();

    const handler = () => fetch();
    window.addEventListener("labels-updated", handler);
    window.addEventListener("storage", handler);
    return () => {
      mounted = false;
      window.removeEventListener("labels-updated", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const createLabel = () => {
    // Open modal for new label
    setEditingLabelId(null);
    setLabelModalName("");
    setLabelModalColor("#536fb6");
    setLabelModalEmail("");
    setLabelModalOpen(true);
  };

  const createBookmark = () => {
    setEditingBookmarkId(null);
    setBookmarkModalName("");
    setBookmarkModalLink("");
    setBookmarkModalOpen(true);
  };

  const openEditBookmark = (bm) => {
    setEditingBookmarkId(bm.id);
    setBookmarkModalName(bm.name || "");
    setBookmarkModalLink(bm.link || "");
    setBookmarkModalOpen(true);
  };

  const saveBookmarkFromModal = useCallback(() => {
    (async () => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const authHeaders = parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;
        const payload = { name: bookmarkModalName.trim(), link: bookmarkModalLink.trim() };
        if (authHeaders) {
          if (editingBookmarkId) {
            await apiUpdateBookmark(editingBookmarkId, payload, authHeaders);
          } else {
            await apiCreateBookmark(payload, authHeaders);
          }
          const bmRes = await apiLoadBookmarks(authHeaders);
          setBookmarks(Array.isArray(bmRes?.bookmarks) ? bmRes.bookmarks : []);
        } else {
          const next = Array.isArray(bookmarks) ? [...bookmarks] : [];
          if (editingBookmarkId) {
            const idx = next.findIndex((b) => String(b.id) === String(editingBookmarkId));
            if (idx >= 0) next[idx] = { ...next[idx], name: bookmarkModalName.trim(), link: bookmarkModalLink.trim() };
          } else {
            const id = Date.now();
            next.push({ id, name: bookmarkModalName.trim(), link: bookmarkModalLink.trim() });
          }
          localStorage.setItem("MAIL_BOOKMARKS", JSON.stringify(next));
          setBookmarks(next);
        }
      } catch (err) {
        console.error("Failed to save bookmark", err);
      }
      setBookmarkModalOpen(false);
      setEditingBookmarkId(null);
      setBookmarkModalName("");
      setBookmarkModalLink("");
    })();
  }, [bookmarkModalName, bookmarkModalLink, bookmarks, editingBookmarkId]);

  const deleteBookmark = useCallback((id) => {
    if (!window.confirm("Delete this bookmark?")) return;
    (async () => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const authHeaders = parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;
        if (authHeaders) {
          await apiDeleteBookmark(id, authHeaders);
          const bmRes = await apiLoadBookmarks(authHeaders);
          setBookmarks(Array.isArray(bmRes?.bookmarks) ? bmRes.bookmarks : []);
        } else {
          const next = (bookmarks || []).filter((b) => String(b.id) !== String(id));
          localStorage.setItem("MAIL_BOOKMARKS", JSON.stringify(next));
          setBookmarks(next);
        }
      } catch (err) {
        console.error("Failed to delete bookmark", err);
      }
    })();
  }, [bookmarks]);

  const openEditLabel = (lbl) => {
    setEditingLabelId(lbl.id);
    setLabelModalName(lbl.name || "");
    setLabelModalColor(lbl.color || "#536fb6");
    setLabelModalEmail(lbl.emailMatch || "");
    setLabelModalOpen(true);
  };

  const saveLabelFromModal = useCallback(async () => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const authHeaders = parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;
      if (!authHeaders) throw new Error("Not authenticated");

      if (editingLabelId) {
        await apiUpdateLabel(editingLabelId, { name: labelModalName.trim(), color: labelModalColor, emailMatch: String(labelModalEmail || "").trim() }, authHeaders);
      } else {
        await apiCreateLabel({ name: labelModalName.trim(), color: labelModalColor, emailMatch: String(labelModalEmail || "").trim() }, authHeaders);
      }

      window.dispatchEvent(new CustomEvent("labels-updated"));
    } catch (err) {
      console.error("Failed to save label", err);
    }
    setLabelModalOpen(false);
    setEditingLabelId(null);
    setLabelModalName("");
  }, [editingLabelId, labelModalName, labelModalColor, labelModalEmail]);

  const deleteLabel = useCallback(async (id) => {
    if (!window.confirm("Delete this label?")) return;
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const authHeaders = parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : null;
      if (!authHeaders) throw new Error("Not authenticated");

      await apiDeleteLabel(id, authHeaders);
      window.dispatchEvent(new CustomEvent("labels-updated"));
    } catch (err) {
      console.error("Failed to delete label", err);
    }
  }, []);

  return (
    <aside
      id="mail-sidebar"
      className={`mail-sidebar ${collapsed ? "collapsed" : ""}`}
    >
      <div className="mail-brand">
       <a href="/" className="mail-brand-link">
        <div className="mail-brand-content">
          <img src="/TimeEdgeIcon.png" alt="TimeEdge Icon" className="mail-brand-icon" />
         
        </div>
        </a>
       
      </div>
     <nav className="mail-nav">
  {navItems.map((item, index) => {
    let count = 0;

    if (item.key === "inbox") count = unreadCount;
    else if (item.key === "drafts") count = draftsCount;
    else if (item.key === "starred") count = starredCount;
    else if (item.key === "archive") count = archiveCount;
    else if (item.key === "trash") count = trashCount;
    else if (item.key === "notes") count = noteCount;

    let content = null;

    // Labels & Books
    if (item.key === "labels" || item.key === "bookmarks") {
      content = (
        <button
          key={item.key}
          className="mail-nav-btn"
          onClick={() => {
            if (item.key === "labels") createLabel();
            if (item.key === "bookmarks") createBookmark();
          }}
        >
          <div className="icon-wrapper">
            <i className={`fa-solid ${item.icon}`}></i>
          </div>

          {!collapsed && (
            <span className="mainslidernotif">{item.label}</span>
          )}
        </button>
      );
    }

    // Profile
    else if (item.key === "profile") {
      content = (
        <button
          key={item.key}
          className="mail-nav-btn"
          title={item.label}
          onClick={() => setShowProfileModal(true)}
        >
          <div className="icon-wrapper">
            <i className={`fa-solid ${item.icon}`}></i>
          </div>

          {!collapsed && (
            <span className="mainslidernotif">{item.label}</span>
          )}
        </button>
      );
    }

    // Lock App
    else if (item.key === "lock") {
      content = (
        <button
          key={item.key}
          className="mail-nav-btn"
          title={item.label}
          onClick={() => {
            const lockEnabled =
              localStorage.getItem("app-lock-enabled") === "true";

            if (lockEnabled && window.__lockApp) {
              window.__lockApp();
            } else {
              navigate("/kmail/settings?focus=app-lock");
            }

            if (typeof onNavigate === "function") {
              onNavigate();
            }
          }}
        >
          <div className="icon-wrapper">
            <i className={`fa-solid ${item.icon}`}></i>
          </div>

          {!collapsed && (
            <span className="mainslidernotif">{item.label}</span>
          )}
        </button>
      );
    }

    // Normal Menu
    else {
      content = (
        <NavLink
          key={item.key}
          to={item.to}
          title={item.label}
          end={item.key === "inbox"}
          className={({ isActive }) =>
            `mail-nav-btn ${isActive ? "active" : ""}`
          }
          onClick={() => {
            if (typeof onNavigate === "function") onNavigate();
          }}
        >
          <div className="icon-wrapper">
            <i className={`fa-solid ${item.icon}`}></i>

            {count > 0 && (
              <span className="icon-count">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </div>

          {!collapsed && (
            <span className="mainslidernotif">{item.label}</span>
          )}
        </NavLink>
      );
    }

    return (
      <React.Fragment key={item.key}>
        {content}

        {/* Divider after Meeting */}
        {item.key === "meeting" && (
          <div className="sidebar-divider" />
        )}

        {/* Divider after Notes */}
        {item.key === "note" && (
          <div className="sidebar-divider" />
        )}
      </React.Fragment>
    );
  })}
</nav>
      {/* BOOKMARKS SECTION */}
      {/* {!collapsed && bookmarks.length > 0 && (
        <div className="mail-sidebar-section">
          <div className="sidebar-section-title">Bookmarks</div>
          <div className="mail-bookmarks-list">
            {bookmarks.map((bm) => (
              <div
                key={bm.id}
                className="mail-bookmark-item"
                onClick={() => {
                  if (bm.link) {
                    window.open(bm.link, "_blank");
                  }
                }}
                title={bm.link}
              >
                <i className="fa-solid fa-bookmark" style={{ marginRight: 8, color: "#f59e0b" }} />
                <span className="mail-bookmark-name">{bm.name}</span>
                <span className="mail-bookmark-actions">
                  <button
                    type="button"
                    className="bookmark-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditBookmark(bm);
                    }}
                    title="Edit"
                  >
                    <i className="fa-solid fa-pen-to-square" />
                  </button>
                  <button
                    type="button"
                    className="bookmark-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteBookmark(bm.id);
                    }}
                    title="Delete"
                  >
                    <i className="fa-solid fa-trash" />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )} */}

 {showProfileModal && (
    <ProfileModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
  )}
   
    {labelModalOpen && (
 <div className="contacts-form-overlay" role="dialog" aria-modal="true">
  <div className="contacts-form">

    <h3 className="form-title">
      {editingLabelId ? "Edit Label" : "New Label"}
    </h3>

    {/* EXISTING LABELS (keep same) */}
    <div className="label-existing-list">
      {labels.map((lbl) => {
        const isActive = location.pathname.includes(`/Kmail/label/${lbl.id}`);

        const count = Object.values(assignments || {})
          .filter((list) => Array.isArray(list) && list.includes(lbl.id)).length;

        return (
          <div
            key={lbl.id}
            className={`mail-label-item ${isActive ? "active" : ""}`}
            onClick={() => {
              navigate(`/Kmail/label/${lbl.id}`);
              if (typeof onNavigate === "function") onNavigate();
            }}
          >
            <span
              className="label-dot"
              style={{ background: lbl.color || "#536fb6" }}
            />

            <span className="mail-label-name">{lbl.name}</span>

            <span className="mail-label-count">
              {count > 0 ? `(${count})` : ""}
            </span>

            <span className="label-actions">
              <button
                type="button"
                className="label-action"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditLabel(lbl);
                }}
              >
                <i className="fa-solid fa-pen-to-square" />
              </button>

              <button
                type="button"
                className="label-action"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteLabel(lbl.id);
                }}
              >
                <i className="fa-solid fa-trash" />
              </button>
            </span>
          </div>
        );
      })}
    </div>

    {/* FORM (Contact Style) */}
    <form className="form-body">

      {/* NAME */}
      <div className="input-group">
        <input
          type="text"
          placeholder=" "
          value={labelModalName}
          onChange={(e) => setLabelModalName(e.target.value)}
        />
        <label>Label Name <span style={{ color: '#ff0000', fontWeight: 'bold' }}>*</span></label>
      </div>

      {/* EMAIL MATCH */}
      <div className="input-group">
        <select
          value={labelModalEmail}
          onChange={(e) => setLabelModalEmail(e.target.value)}
        >
          <option value=""></option>
          {recentEmails?.map((em) => (
            <option key={em} value={em}>
              {em}
            </option>
          ))}
        </select>
        <label>Match Email (optional)</label>
      </div>

      {/* COLOR */}
      <div className="input-group">
        <input
          type="color"
          value={labelModalColor}
          onChange={(e) => setLabelModalColor(e.target.value)}
        />
        <label>Color <span style={{ color: '#ff0000', fontWeight: 'bold' }}>*</span></label>
      </div>

      {/* BUTTONS */}
      <div className="form-buttons">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setLabelModalOpen(false)}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn-primary"
          onClick={saveLabelFromModal}
          disabled={!labelModalName.trim()}
        >
          Save
        </button>
      </div>

    </form>

  </div>
</div>
)}

     {bookmarkModalOpen && (
 <div className="contacts-form-overlay" role="dialog" aria-modal="true">
  <div className="contacts-form">

    <h3 className="form-title">
      {editingBookmarkId ? "Edit Bookmark" : "New Bookmark"}
    </h3>

    {/* EXISTING BOOKMARKS (same) */}
    <div className="label-existing-list">
      {bookmarks.map((bm) => (
        <div
          key={bm.id}
          className="mail-label-item"
          onClick={() => {
            if (bm.link) {
              window.open(bm.link, "_blank");
            }
          }}
        >
          <span className="mail-label-name">{bm.name}</span>

          {!collapsed && (
            <span className="label-actions">
              <button
                type="button"
                className="label-action"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditBookmark(bm);
                }}
                title="Edit bookmark"
              >
                <i className="fa-solid fa-pen-to-square" />
              </button>

              <button
                type="button"
                className="label-action"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteBookmark(bm.id);
                }}
                title="Delete bookmark"
              >
                <i className="fa-solid fa-trash" />
              </button>
            </span>
          )}
        </div>
      ))}

      {bookmarks.length === 0 && (
        <div className="mail-settings-status">No bookmarks</div>
      )}
    </div>

    {/* FORM (Contact Style) */}
    <form className="form-body">

      {/* NAME */}
      <div className="input-group">
        <input
          type="text"
          placeholder=" "
          value={bookmarkModalName}
          onChange={(e) => setBookmarkModalName(e.target.value)}
        />
        <label>Bookmark Name <span style={{ color: '#ff0000', fontWeight: 'bold' }}>*</span></label>
      </div>

      {/* LINK */}
      <div className="input-group">
        <input
          type="text"
          placeholder=" "
          value={bookmarkModalLink}
          onChange={(e) => setBookmarkModalLink(e.target.value)}
        />
        <label>Link (URL) <span style={{ color: '#ff0000', fontWeight: 'bold' }}>*</span></label>
      </div>

      {/* BUTTONS */}
      <div className="form-buttons">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setBookmarkModalOpen(false)}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn-primary"
          onClick={saveBookmarkFromModal}
          disabled={
            !bookmarkModalName.trim() || !bookmarkModalLink.trim()
          }
        >
          Save
        </button>
      </div>

    </form>

  </div>
</div>
)}


    
      {/* <StorageProgressBar /> */}
    </aside>
  );
}

export default Sidebar;
